import { useState, useRef, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, X } from "lucide-react";

const FIELDS = [
  { key: "name", label: "Name", required: true },
  { key: "phone", label: "Phone", required: true },
  { key: "loan_type", label: "Loan Type", required: true },
  { key: "loan_amount", label: "Loan Amount", required: true },
  { key: "monthly_income", label: "Monthly Income", required: false },
  { key: "email", label: "Email", required: false },
  { key: "city", label: "City", required: false },
  { key: "notes", label: "Notes", required: false },
];
const NUMERIC = new Set(["loan_amount", "monthly_income"]);
const IGNORE = "__ignore__";

function autoMatch(header) {
  const h = String(header).toLowerCase().replace(/[^a-z]/g, "");
  const map = { name: "name", fullname: "name", customername: "name", phone: "phone", mobile: "phone", contact: "phone", phonenumber: "phone", loantype: "loan_type", type: "loan_type", loanamount: "loan_amount", amount: "loan_amount", income: "monthly_income", monthlyincome: "monthly_income", salary: "monthly_income", email: "email", emailid: "email", city: "city", location: "city", notes: "notes", remarks: "notes", comment: "notes" };
  return map[h] || IGNORE;
}

export default function ImportLeads() {
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", blankrows: false });
        if (!json.length) { toast.error("File is empty"); return; }
        const hdr = json[0].map((h) => String(h).trim());
        const dataRows = json.slice(1).filter((r) => r.some((c) => String(c).trim() !== ""));
        const initMap = {};
        hdr.forEach((h, i) => { const f = autoMatch(h); if (f !== IGNORE && !Object.values(initMap).includes(f)) initMap[i] = f; });
        setFileName(file.name); setHeaders(hdr); setRows(dataRows); setMapping(initMap); setResult(null);
      } catch (err) {
        toast.error("Could not read file. Use a valid CSV or Excel file.");
      }
    };
    reader.readAsBinaryString(file);
  };

  const leads = useMemo(() => {
    const colFor = (fieldKey) => Object.keys(mapping).find((i) => mapping[i] === fieldKey);
    return rows.map((r) => {
      const obj = {};
      FIELDS.forEach((f) => {
        const ci = colFor(f.key);
        let v = ci != null ? r[ci] : undefined;
        if (v === "" || v == null) { v = undefined; }
        else if (NUMERIC.has(f.key)) { v = Number(String(v).replace(/[^0-9.]/g, "")) || 0; }
        else { v = String(v).trim(); }
        if (v !== undefined) obj[f.key] = v;
      });
      return obj;
    });
  }, [rows, mapping]);

  const validation = useMemo(() => {
    return leads.map((l) => {
      const errs = [];
      if (!l.name) errs.push("name");
      if (!l.phone) errs.push("phone");
      if (!l.loan_type) errs.push("loan_type");
      if (!l.loan_amount) errs.push("loan_amount");
      return errs;
    });
  }, [leads]);

  const validLeads = useMemo(() => leads.filter((_, i) => validation[i].length === 0), [leads, validation]);
  const invalidCount = validation.filter((e) => e.length > 0).length;

  const upload = useMutation({
    mutationFn: async () => (await api.post("/leads/bulk", { leads: validLeads })).data,
    onSuccess: (d) => {
      setResult(d);
      const n = d?.created ?? d?.inserted ?? d?.count ?? validLeads.length;
      // record batch metadata locally so Import History can show the filename + counts
      try {
        const key = "ls_import_batches";
        const prev = JSON.parse(localStorage.getItem(key) || "[]");
        prev.unshift({ batch_id: d?.batch_id, filename: fileName, created: n, skipped: leads.length - validLeads.length, at: new Date().toISOString() });
        localStorage.setItem(key, JSON.stringify(prev.slice(0, 100)));
      } catch { /* ignore */ }
      toast.success(`Imported ${n} lead${n === 1 ? "" : "s"} — synced to the mobile app.`);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const reset = () => { setFileName(""); setHeaders([]); setRows([]); setMapping({}); setResult(null); if (inputRef.current) inputRef.current.value = ""; };

  return (
    <div data-testid="import-leads-page">
      <PageHeader title="Import Leads" subtitle="Bulk upload leads from a CSV or Excel file. New leads sync to the mobile app instantly." />

      {!fileName && (
        <Card className="p-10 border-2 border-dashed border-slate-300 dark:border-slate-700 text-center max-w-2xl mx-auto"
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}>
          <UploadCloud className="h-12 w-12 mx-auto text-blue-500 mb-4" />
          <div className="font-medium text-slate-800 dark:text-slate-200 mb-1">Drop your CSV / Excel file here</div>
          <div className="text-sm text-slate-500 mb-4">Required columns: Name, Phone, Loan Type, Loan Amount</div>
          <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="import-file-input" onChange={(e) => handleFile(e.target.files?.[0])} />
          <Button onClick={() => inputRef.current?.click()} data-testid="import-browse-button" className="bg-blue-700 hover:bg-blue-800 text-white"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Choose file</Button>
        </Card>
      )}

      {fileName && (
        <div className="space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-sm"><FileSpreadsheet className="h-4 w-4 text-blue-600" /><span className="font-medium">{fileName}</span><Badge variant="outline">{rows.length} rows</Badge></div>
            <Button variant="ghost" size="sm" onClick={reset} data-testid="import-reset-button"><X className="h-4 w-4 mr-1" />Start over</Button>
          </div>

          <Card className="p-5 border-slate-200 dark:border-slate-800">
            <div className="font-heading font-semibold mb-3">Map columns</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {FIELDS.map((f) => {
                const selectedCol = Object.keys(mapping).find((i) => mapping[i] === f.key) ?? IGNORE;
                return (
                  <div key={f.key} className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-600 dark:text-slate-300">{f.label}{f.required && <span className="text-rose-500"> *</span>}</label>
                    <Select value={String(selectedCol)} onValueChange={(v) => {
                      const next = { ...mapping };
                      Object.keys(next).forEach((i) => { if (next[i] === f.key) delete next[i]; });
                      if (v !== IGNORE) next[v] = f.key;
                      setMapping(next);
                    }}>
                      <SelectTrigger data-testid={`import-map-${f.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={IGNORE}>— Not mapped —</SelectItem>
                        {headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h || `Column ${i + 1}`}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          </Card>

          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1.5 text-emerald-600"><CheckCircle2 className="h-4 w-4" />{validLeads.length} valid</span>
            {invalidCount > 0 && <span className="flex items-center gap-1.5 text-amber-600"><AlertTriangle className="h-4 w-4" />{invalidCount} row(s) missing required fields (will be skipped)</span>}
          </div>

          <Card className="border-slate-200 dark:border-slate-800 overflow-hidden p-0">
            <div className="max-h-[420px] overflow-auto">
              <Table data-testid="import-preview-table">
                <TableHeader><TableRow className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50">
                  <TableHead className="text-xs uppercase w-8"></TableHead>
                  {FIELDS.map((f) => <TableHead key={f.key} className="text-xs uppercase">{f.label}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {leads.slice(0, 100).map((l, i) => {
                    const bad = validation[i].length > 0;
                    return (
                      <TableRow key={i} className={bad ? "bg-amber-50/60 dark:bg-amber-950/20" : ""}>
                        <TableCell>{bad ? <AlertTriangle className="h-4 w-4 text-amber-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}</TableCell>
                        {FIELDS.map((f) => <TableCell key={f.key} className={`text-sm ${validation[i].includes(f.key) ? "text-rose-500 font-medium" : "text-slate-700 dark:text-slate-300"}`}>{validation[i].includes(f.key) ? "missing" : (l[f.key] ?? "—")}</TableCell>)}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {leads.length > 100 && <div className="text-xs text-slate-400 text-center py-2 border-t border-slate-100 dark:border-slate-800">Showing first 100 of {leads.length} rows — all valid rows will be imported.</div>}
          </Card>

          <div className="flex justify-end">
            <Button onClick={() => upload.mutate()} disabled={validLeads.length === 0 || upload.isPending || !!result} data-testid="import-submit-button" className="bg-blue-700 hover:bg-blue-800 text-white">
              <UploadCloud className="h-4 w-4 mr-1.5" />{upload.isPending ? "Importing…" : `Import ${validLeads.length} lead${validLeads.length === 1 ? "" : "s"}`}
            </Button>
          </div>

          {result && (
            <Card className="p-5 border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/50 dark:bg-emerald-950/20" data-testid="import-result">
              <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 font-medium"><CheckCircle2 className="h-5 w-5" />Import complete</div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{result?.created ?? result?.inserted ?? result?.count ?? validLeads.length} leads were added and are now visible in both the web and mobile app.</p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
